<template>
  <div>
    <div class="mb-8 text-headline-xs">{{ title }}</div>
    <div
      v-for="(feature, index) in formData.features"
      :key="`${feature._vid}-${formData.features.length}`"
      class="relative mb-2"
      :class="{ hidden: feature._destroy }"
    >
      <SortBtnGroup
        class="absolute -top-2 right-0"
        :move-up-disabled="index === 0"
        :move-down-disabled="index === formData.features.length - 1"
        @destroy="destroy(index)"
        @move-up="moveUp(index)"
        @move-down="moveDown(index)"
      />
      <FeatureInput v-model="formData.features[index]" :options="availableFeatures" :selected-features="getSelectedFeatures(index)" />
    </div>
    <div v-if="noFeatures" class="relative mb-2">
      <SortBtnGroup class="absolute -top-2 right-0" move-up-disabled move-down-disabled />
      <FieldWrapper for="feature-add-select" class="mb-8" :label="t('Reviews.agency.edit.feature.label').toString()">
        <template #default="{ id }">
          <SelectField :id="id" :placeholder="t('Reviews.agency.edit.feature.placeholder').toString()" @update:model-value="addFeature">
            <option v-for="option in availableFeatures" :key="option.id" :value="option.id">{{ option.name }}</option>
          </SelectField>
        </template>
      </FieldWrapper>
    </div>
    <button
      v-if="canAddFeature"
      class="btn-outline-color-s mb-4 w-full text-nowrap"
      data-testid="agency-add-service-button"
      @click.prevent="addFeature()"
    >
      <CirclePlusIcon class="mr-2" />
      {{ t('Reviews.agency.edit.industries.actions.addService') }}
    </button>
  </div>
</template>
<script setup lang="ts">
import { FieldWrapper, SelectField } from '@ramp106/omrjs-core-ui'
import { CirclePlusIcon } from '@ramp106/omrjs-core-ui/icons'
import { useVuelidate } from '@vuelidate/core'
import { v4 as uuidv4 } from 'uuid'
import { computed, reactive } from 'vue'
import { useI18n } from 'vue-i18n'

import { usePositionedArray } from '@/composables/usePositionedArray'
import SortBtnGroup from '@/ui/SortBtnGroup.vue'

import FeatureInput from './FeatureInput.vue'
import type { FeatureFormData } from './types'

const FEATURES_PER_CATEGORY_LIMIT = 15

const props = defineProps<{
  categoryId: string
  categories: { id: string; title: string }[]
  features: FeatureFormData[]
  availableFeatures: { name: string; id: string }[]
}>()

const { t } = useI18n()

const title = computed(() => props.categories.find(({ id }) => id === props.categoryId)?.title)

const formData = reactive<{ features: FeatureFormData[] }>({
  features: props.features,
})

const rules = computed(() => ({
  features: {},
}))

useVuelidate(rules, formData)

const activeFeaturesCount = computed(() => formData.features.filter((i) => !i._destroy).length)

const noFeatures = computed(() => activeFeaturesCount.value === 0)

const canAddFeature = computed(() => activeFeaturesCount.value < FEATURES_PER_CATEGORY_LIMIT)

const { create, destroy, moveUp, moveDown } = usePositionedArray(formData.features, 'featureId')

function addFeature(featureId: string = '') {
  create({ featureId, categoryId: props.categoryId, _vid: uuidv4() })
}

function getSelectedFeatures(excludeIndex: number) {
  return formData.features.filter((i, index) => index !== excludeIndex && !i._destroy).map((i) => i.featureId)
}
</script>
